import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { DashboardLayout } from "../../../components/dashboard/layouts/DashboardLayout";
import {
  ArrowLeftStartOnRectangleIcon,
  ArrowPathIcon,
  ArrowRightStartOnRectangleIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  CheckIcon,
  CreditCardIcon,
  CurrencyEuroIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  PencilIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  PlusIcon,
  TagIcon,
  TicketIcon,
  TrashIcon,
  UserCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useState, Fragment, useEffect } from "react";
import { Dialog, Transition, Menu } from "@headlessui/react";
import { classNames } from "../../../utils/styles";
import QRCode from "react-qr-code";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../../store/store";
import { dashboardActions } from "../../../store/dashboard";
import { format } from "date-fns";
import { SkeletonLoading } from "../../../components/SkeletonLoading";
import {
  DashboardAttendee,
  EmailMessage,
  InvoicePayment,
  PurchasedAddon,
  PurchasedAddonInstance,
  TicketAnswer,
} from "../../../models/Dashboard";
import { useDarkMode } from "../../../hooks";
import { toHeaderCase } from "js-convert-case";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Input } from "../../../crud/form/Input";
import { ButtonSpinner } from "../../../components/ButtonSpinner";
import { checkIn, updateOrder } from "../../../dashboardQueries";
import toast from "react-hot-toast";
import { OrderAttendeeRow } from "../../../components/dashboard/orders/OrderAttendeeRow";
import { OrderAddonRow } from "../../../components/dashboard/orders/OrderAddonRow";
import { Modal } from "@/components/dashboard/Modal";
import axios from "axios";
import { useMutation } from "react-query";
import { BASE_URL } from "@/dashboardQueries";
import { Button } from "@/components/ui/button";

export const ViewOrder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { orderId } = useParams();
  const contentLoading = useSelector(
    (state: State) => state.dashboard.contentLoading
  );
  const order = useSelector((state: State) => state.dashboard.order);
  const orderEmails = useSelector(
    (state: State) => state.dashboard.orderEmails
  );
  const [viewQRCodeModalOpen, setViewQRCodeModalOpen] = useState(false);
  const [viewingAttendee, setViewingAttendee] = useState<any>({});
  const isDarkMode = useDarkMode();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // React Query mutation for deleting an order
  const deleteOrderMutation = useMutation(
    async (orderId: string | number) => {
      await axios.delete(`${BASE_URL}/dashboard/orders/${orderId}`);
    },
    {
      onSuccess: () => {
        toast.success("Successfully deleted order");
        navigate("/dashboard/orders");
      },
    }
  );

  useEffect(() => {
    if (!orderId) {
      navigate("/");
    } else {
      dispatch(dashboardActions.GetOrder({ orderId: orderId }));
    }
  }, []);

  // When we have the order, we can get the emails associated to this email then
  useEffect(() => {
    if (order) {
      dispatch(
        dashboardActions.GetOrderEmails({
          recipient: order.emailAddress.toLowerCase(),
        })
      );
    }
  }, [order]);

  const handleEditSubmit = async (values: any) => {
    if (order) {
      setIsLoading(true);
      try {
        await updateOrder(order?.id, values);
        dispatch(dashboardActions.GetOrder({ orderId: order.id.toString() }));
        toast.success("Order updated successfully.");
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        toast.error("An error occurred. Please try again.");
        return;
      }
      setIsEditing(false);
    }
  };

  /* 
    Ideas for this page:
    - Refund an order (add a new refunded status)
    - Change email, first name and last name
    
    For payment plans:
    - View payments related to this order
    - Cancel the plan
    - Change the default payment method
    - Charge an instalment manually
  */

  return (
    <DashboardLayout requiredPermissions={["view_order"]} pageTitle="Orders">
      {order && (
        <>
          {/* View QR Code Modal */}
          <Modal
            isOpen={viewQRCodeModalOpen}
            setIsOpen={(isOpen: boolean) => setViewQRCodeModalOpen(isOpen)}
          >
            <div className="p-3 rounded-md bg-zinc-800">
              <div className="flex items-center justify-center">
                <QRCode
                  className="w-32 h-32"
                  value={viewingAttendee?.uuid || ""}
                  bgColor={"transparent"}
                  fgColor={"white"}
                />
              </div>
              <div className="mt-3 text-center">
                <h1 className="text-lg font-semibold text-gray-200">
                  {viewingAttendee?.ticket?.name}
                </h1>
              </div>
            </div>
            <div className="w-full mt-4">
              <Button
                className="w-full"
                onClick={() => setViewQRCodeModalOpen(false)}
              >
                Close
              </Button>
            </div>
          </Modal>
          {/* Delete Modal */}
          <Modal
            isOpen={!!deleteModalOpen}
            setIsOpen={(isOpen: boolean) => setDeleteModalOpen(isOpen)}
          >
            <div className="w-full max-w-lg">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon
                    className="h-6 w-6 text-red-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <Dialog.Title
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                  >
                    Delete Order for {order.firstName} {order.lastName}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete this order? All of the
                      tickets and data related to it will be deleted forever and
                      any issued tickets will no longer work.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:ml-10 sm:mt-4 sm:flex sm:pl-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsDeleting(true);
                    deleteOrderMutation.mutate(order.id);
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto"
                >
                  {isDeleting ? (
                    <div className="flex items-center justify-center">
                      <ButtonSpinner />
                      <>Deleting...</>
                    </div>
                  ) : (
                    "Yes, delete"
                  )}
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:ml-3 sm:mt-0 sm:w-auto"
                  onClick={() => setDeleteModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
          <header className="relative isolate">
            <div
              className="absolute inset-0 -z-10 overflow-hidden"
              aria-hidden="true"
            >
              <div className="absolute left-16 top-full -mt-16 transform-gpu opacity-50 blur-3xl xl:left-1/2 xl:-ml-80">
                <div
                  className="aspect-[1154/678] w-[72.125rem] bg-gradient-to-br from-[#FF80B5] to-[#9089FC]"
                  style={{
                    clipPath:
                      "polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 45.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)",
                  }}
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 h-px bg-dark-primary/5" />
            </div>

            <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 border-b border-transparent dark:border-white/5">
              <div className="mx-auto flex max-w-2xl items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
                <div className="flex items-center gap-x-6">
                  {contentLoading ? (
                    <div role="status" className="max-w-lg animate-pulse">
                      <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-2.5"></div>
                      <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-80 mb-4"></div>
                    </div>
                  ) : (
                    <h1>
                      <div className="text-sm leading-6 text-gray-500 dark:text-gray-400">
                        Order #{order.uuid}
                      </div>
                      <div className="mt-1 text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        {order?.firstName} {order?.lastName}
                      </div>
                    </h1>
                  )}
                </div>
                <div className="flex items-center gap-x-4 sm:gap-x-6">
                  <button
                    onClick={() =>
                      dispatch(
                        dashboardActions.DownloadOrderQRCodes({
                          orderUUID: order.uuid,
                        })
                      )
                    }
                    type="button"
                    className="hidden lg:block text-sm font-semibold leading-6 text-gray-900 dark:text-gray-400"
                  >
                    Export QR Codes
                  </button>
                  <button
                    onClick={() =>
                      dispatch(
                        dashboardActions.ResendOrderConfirmtion({
                          orderUUID: order.uuid,
                        })
                      )
                    }
                    type="button"
                    className="hidden lg:block rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    Resend Confirmation Email
                  </button>
                  <button
                    onClick={() => setDeleteModalOpen(true)}
                    type="button"
                    className="inline-flex items-center rounded-md bg-red-400/10 px-2 py-1.5 text-xs font-medium text-red-400 ring-1 ring-inset ring-red-400/20 -ml-3"
                  >
                    <TrashIcon className="text-red-500 h-6 w-6" />
                  </button>

                  <Menu as="div" className="relative lg:hidden">
                    <Menu.Button className="-m-3 block p-3">
                      <span className="sr-only">More</span>
                      <EllipsisVerticalIcon
                        className="h-5 w-5 text-gray-500"
                        aria-hidden="true"
                      />
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="dark:bg-dark-secondary dark:ring-white/5 absolute right-0 z-10 mt-0.5 w-52 text-right origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              className={classNames(
                                active ? "bg-gray-50" : "",
                                "block w-full px-3 pb-2 py-1 text-sm leading-6 text-gray-900 text-right dark:text-white dark:hover:bg-dark-secondary dark:hover:text-gray-400 border-b dark:border-white/5"
                              )}
                            >
                              Export QR Codes
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() =>
                                dispatch(
                                  dashboardActions.ResendOrderConfirmtion({
                                    orderUUID: order.uuid,
                                  })
                                )
                              }
                              className={classNames(
                                active ? "bg-gray-50" : "",
                                "block px-3 py-1 pt-2 text-sm leading-6 text-gray-900 text-right dark:text-white dark:hover:bg-dark-secondary dark:hover:text-gray-400"
                              )}
                            >
                              Resend Confirmation Email
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>
          </header>

          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 items-start gap-x-8 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {/* Invoice summary */}
              <div className="lg:col-start-3 lg:row-end-1">
                <h2 className="sr-only">Summary</h2>

                {/* Payment Summary */}
                <div className="rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5 dark:bg-transparent dark:border dark:border-white/5 pb-1">
                  <dl className="flex flex-wrap mb-5">
                    <div className="flex-auto pl-6 pt-6">
                      <dt className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                        Amount
                      </dt>
                      <dd className="mt-1 text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        {contentLoading ? (
                          <div
                            role="status"
                            className="max-w-lg animate-puls mt-4"
                          >
                            <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                          </div>
                        ) : (
                          <>&euro;{order?.total}</>
                        )}
                      </dd>
                    </div>
                    <div className="flex-none self-end px-6 pt-4">
                      <dt className="sr-only">Status</dt>
                      <dd className="rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-inset ring-green-600/20">
                        Paid
                      </dd>
                    </div>
                    <div className="mt-6 flex w-full flex-none gap-x-4 border-t border-gray-900/5 dark:border-white/5 px-6 pt-6">
                      <dt className="flex-none">
                        <span className="sr-only">Client</span>
                        <UserCircleIcon
                          className="h-6 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </dt>
                      <dd className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                        {contentLoading ? (
                          <div
                            role="status"
                            className="max-w-lg animate-puls mt-4"
                          >
                            <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                          </div>
                        ) : (
                          <>
                            {order?.firstName} {order?.lastName}
                          </>
                        )}
                      </dd>
                    </div>
                    <div className="mt-4 flex w-full flex-none gap-x-4 px-6">
                      <dt className="flex-none">
                        <span className="sr-only">Paid on</span>
                        <CalendarDaysIcon
                          className="h-6 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </dt>
                      <dd className="text-sm leading-6 text-gray-500 dark:text-gray-400">
                        <time>
                          {contentLoading ? (
                            <div
                              role="status"
                              className="max-w-lg animate-puls mt-4"
                            >
                              <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                            </div>
                          ) : (
                            <>
                              {order?.paymentCompletedAt && (
                                <>
                                  {format(
                                    new Date(order?.paymentCompletedAt),
                                    "dd LLL yyyy @ HH:mm"
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </time>
                      </dd>
                    </div>
                    {order?.cardBrand && order?.cardLast4 && (
                      <>
                        <div className="mt-4 flex w-full flex-none gap-x-4 px-6">
                          <dt className="flex-none">
                            <span className="sr-only">Status</span>
                            <CreditCardIcon
                              className="h-6 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                          </dt>
                          <dd className="text-sm leading-6 text-gray-500 dark:text-gray-400">
                            {contentLoading ? (
                              <div
                                role="status"
                                className="max-w-lg animate-puls mt-4"
                              >
                                <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                              </div>
                            ) : (
                              <>
                                <div className="flex">
                                  Paid with
                                  {order.cardBrand == "visa" && (
                                    <svg
                                      width="30"
                                      height="20"
                                      enable-background="new 0 0 780 500"
                                      version="1.1"
                                      viewBox="0 0 780 500"
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="ml-1"
                                    >
                                      <path
                                        d="M40,0h700c22.092,0,40,17.909,40,40v420c0,22.092-17.908,40-40,40H40c-22.091,0-40-17.908-40-40V40   C0,17.909,17.909,0,40,0z"
                                        fill="#0E4595"
                                      />
                                      <path
                                        d="m293.2 348.73l33.361-195.76h53.36l-33.385 195.76h-53.336zm246.11-191.54c-10.57-3.966-27.137-8.222-47.822-8.222-52.725 0-89.865 26.55-90.18 64.603-0.299 28.13 26.514 43.822 46.752 53.186 20.771 9.595 27.752 15.714 27.654 24.283-0.131 13.121-16.586 19.116-31.922 19.116-21.357 0-32.703-2.967-50.227-10.276l-6.876-3.11-7.489 43.823c12.463 5.464 35.51 10.198 59.438 10.443 56.09 0 92.5-26.246 92.916-66.882 0.199-22.269-14.016-39.216-44.801-53.188-18.65-9.055-30.072-15.099-29.951-24.268 0-8.137 9.668-16.839 30.557-16.839 17.449-0.27 30.09 3.535 39.938 7.5l4.781 2.26 7.232-42.429m137.31-4.223h-41.232c-12.773 0-22.332 3.487-27.941 16.234l-79.244 179.4h56.031s9.16-24.123 11.232-29.418c6.125 0 60.555 0.084 68.338 0.084 1.596 6.853 6.49 29.334 6.49 29.334h49.514l-43.188-195.64zm-65.418 126.41c4.412-11.279 21.26-54.723 21.26-54.723-0.316 0.522 4.379-11.334 7.074-18.684l3.605 16.879s10.219 46.729 12.354 56.528h-44.293zm-363.3-126.41l-52.24 133.5-5.567-27.13c-9.725-31.273-40.025-65.155-73.898-82.118l47.766 171.2 56.456-0.064 84.004-195.39h-56.521"
                                        fill="#fff"
                                      />
                                      <path
                                        d="m146.92 152.96h-86.041l-0.681 4.073c66.938 16.204 111.23 55.363 129.62 102.41l-18.71-89.96c-3.23-12.395-12.597-16.094-24.186-16.527"
                                        fill="#F2AE14"
                                      />
                                    </svg>
                                  )}
                                  {order.cardBrand == "mastercard" && (
                                    <svg
                                      width="30"
                                      height="20"
                                      enable-background="new 0 0 780 500"
                                      version="1.1"
                                      viewBox="0 0 780 500"
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="ml-1"
                                    >
                                      <path
                                        d="M40,0h700c22.092,0,40,17.909,40,40v420c0,22.092-17.908,40-40,40H40c-22.091,0-40-17.908-40-40V40   C0,17.909,17.909,0,40,0z"
                                        fill="#16366F"
                                      />
                                      <path
                                        d="m449.01 250c0 99.143-80.37 179.5-179.51 179.5s-179.5-80.361-179.5-179.5c0-99.133 80.362-179.5 179.5-179.5 99.137 0 179.51 80.37 179.51 179.5"
                                        fill="#D9222A"
                                      />
                                      <path
                                        d="m510.49 70.496c-46.38 0-88.643 17.596-120.5 46.466-6.49 5.889-12.548 12.237-18.125 18.996h36.266c4.966 6.037 9.536 12.388 13.685 19.013h-63.635c-3.827 6.121-7.28 12.469-10.341 19.008h84.312c2.893 6.185 5.431 12.53 7.6 19.004h-99.512c-2.091 6.235-3.832 12.581-5.217 19.009h109.94c2.689 12.49 4.044 25.231 4.041 38.008 0 19.934-3.254 39.113-9.254 57.02h-99.512c2.164 6.479 4.7 12.825 7.595 19.01h84.317c-3.064 6.54-6.52 12.889-10.347 19.013h-63.625c4.154 6.629 8.73 12.979 13.685 18.996h36.258c-5.57 6.772-11.63 13.126-18.13 19.012 31.86 28.867 74.118 46.454 120.5 46.454 99.138-1e-3 179.51-80.362 179.51-179.5 0-99.13-80.37-179.5-179.51-179.5"
                                        fill="#EE9F2D"
                                      />
                                      <path d="m666.08 350.06c0-3.201 2.592-5.801 5.796-5.801s5.796 2.6 5.796 5.801c0 3.199-2.592 5.799-5.796 5.799-3.202-1e-3 -5.797-2.598-5.796-5.799zm5.796 4.408c2.435-1e-3 4.407-1.975 4.408-4.408 0-2.433-1.972-4.404-4.404-4.404h-4e-3c-2.429-4e-3 -4.4 1.963-4.404 4.392v0.013c-3e-3 2.432 1.967 4.406 4.399 4.408 1e-3 -1e-3 3e-3 -1e-3 5e-3 -1e-3zm-0.783-1.86h-1.188v-5.094h2.149c0.45 0 0.908 0 1.305 0.254 0.413 0.278 0.646 0.77 0.646 1.278 0 0.57-0.337 1.104-0.883 1.312l0.937 2.25h-1.315l-0.78-2.016h-0.87v2.016h-1e-3zm0-2.89h0.658c0.246 0 0.504 0.02 0.725-0.1 0.196-0.125 0.296-0.359 0.296-0.584 0-0.195-0.12-0.42-0.288-0.516-0.207-0.131-0.536-0.101-0.758-0.101h-0.633v1.301zm-443.5-80.063c-2.045-0.237-2.945-0.301-4.35-0.301-11.045 0-16.637 3.789-16.637 11.268 0 4.611 2.73 7.546 6.987 7.546 7.938 0 13.659-7.56 14-18.513zm14.171 32.996h-16.146l0.371-7.676c-4.925 6.067-11.496 8.95-20.425 8.95-10.562 0-17.804-8.25-17.804-20.229 0-18.024 12.596-28.54 34.217-28.54 2.208 0 5.041 0.2 7.941 0.569 0.605-2.441 0.763-3.486 0.763-4.8 0-4.908-3.396-6.738-12.5-6.738-9.533-0.108-17.396 2.271-20.625 3.334 0.204-1.23 2.7-16.658 2.7-16.658 9.712-2.846 16.117-3.917 23.325-3.917 16.733 0 25.596 7.512 25.58 21.712 0.032 3.805-0.597 8.5-1.58 14.671-1.692 10.731-5.32 33.718-5.817 39.322zm-62.158 0h-19.488l11.163-69.997-24.925 69.997h-13.28l-1.64-69.597-11.734 69.597h-18.242l15.238-91.054h28.02l1.7 50.966 17.092-50.966h31.167l-15.071 91.054m354.98-32.996c-2.037-0.237-2.942-0.301-4.342-0.301-11.041 0-16.634 3.789-16.634 11.268 0 4.611 2.726 7.546 6.983 7.546 7.939 0 13.664-7.56 13.993-18.513zm14.183 32.996h-16.145l0.365-7.676c-4.925 6.067-11.5 8.95-20.42 8.95-10.566 0-17.8-8.25-17.8-20.229 0-18.024 12.587-28.54 34.212-28.54 2.208 0 5.037 0.2 7.934 0.569 0.604-2.441 0.763-3.486 0.763-4.8 0-4.908-3.392-6.738-12.496-6.738-9.533-0.108-17.388 2.271-20.63 3.334 0.205-1.23 2.709-16.658 2.709-16.658 9.713-2.846 16.113-3.917 23.312-3.917 16.741 0 25.604 7.512 25.588 21.712 0.032 3.805-0.597 8.5-1.58 14.671-1.682 10.731-5.32 33.718-5.812 39.322zm-220.39-1.125c-5.334 1.68-9.492 2.399-14 2.399-9.963 0-15.4-5.725-15.4-16.267-0.142-3.27 1.433-11.879 2.67-19.737 1.125-6.917 8.45-50.53 8.45-50.53h19.371l-2.262 11.209h11.7l-2.643 17.796h-11.742c-2.25 14.083-5.454 31.625-5.491 33.95 0 3.817 2.037 5.483 6.67 5.483 2.221 0 3.941-0.226 5.255-0.7l-2.578 16.397m59.391-0.6c-6.654 2.033-13.075 3.017-19.879 3-21.683-0.021-32.987-11.346-32.987-33.032 0-25.313 14.38-43.947 33.9-43.947 15.97 0 26.17 10.433 26.17 26.796 0 5.429-0.7 10.729-2.387 18.212h-38.575c-1.304 10.742 5.57 15.217 16.837 15.217 6.935 0 13.188-1.43 20.142-4.663l-3.221 18.417zm-10.887-43.9c0.107-1.543 2.054-13.217-9.013-13.217-6.171 0-10.583 4.704-12.38 13.217h21.393zm-123.42-5.017c0 9.367 4.541 15.825 14.841 20.676 7.892 3.709 9.113 4.809 9.113 8.17 0 4.617-3.48 6.7-11.192 6.7-5.812 0-11.22-0.907-17.458-2.92 0 0-2.563 16.32-2.68 17.101 4.43 0.966 8.38 1.861 20.28 2.19 20.562 0 30.058-7.829 30.058-24.75 0-10.175-3.975-16.146-13.737-20.633-8.171-3.75-9.109-4.588-9.109-8.046 0-4.004 3.238-6.046 9.538-6.046 3.825 0 9.05 0.408 14 1.113l2.775-17.175c-5.046-0.8-12.696-1.442-17.15-1.442-21.8 0-29.346 11.387-29.279 25.062m229.09-23.116c5.413 0 10.459 1.42 17.413 4.92l3.187-19.762c-2.854-1.12-12.904-7.7-21.416-7.7-13.042 0-24.066 6.47-31.82 17.15-11.31-3.746-15.959 3.825-21.659 11.367l-5.062 1.179c0.383-2.483 0.73-4.95 0.613-7.446h-17.896c-2.445 22.917-6.779 46.13-10.171 69.075l-0.884 4.976h19.496c3.254-21.143 5.038-34.681 6.121-43.842l7.342-4.084c1.096-4.08 4.529-5.458 11.416-5.292-0.926 5.008-1.389 10.09-1.383 15.184 0 24.225 13.071 39.308 34.05 39.308 5.404 0 10.042-0.712 17.221-2.657l3.431-20.76c-6.46 3.18-11.761 4.676-16.561 4.676-11.328 0-18.183-8.362-18.183-22.184-1e-3 -20.05 10.195-34.108 24.745-34.108" />
                                      <path
                                        d="m185.21 297.24h-19.491l11.17-69.988-24.925 69.988h-13.282l-1.642-69.588-11.733 69.588h-18.243l15.238-91.042h28.02l0.788 56.362 18.904-56.362h30.267l-15.071 91.042"
                                        fill="#fff"
                                      />
                                      <path d="m647.52 211.6l-4.319 26.308c-5.33-7.012-11.054-12.087-18.612-12.087-9.834 0-18.784 7.454-24.642 18.425-8.158-1.692-16.597-4.563-16.597-4.563l-4e-3 0.067c0.658-6.133 0.92-9.875 0.862-11.146h-17.9c-2.437 22.917-6.77 46.13-10.157 69.075l-0.893 4.976h19.492c2.633-17.097 4.65-31.293 6.133-42.551 6.659-6.017 9.992-11.267 16.721-10.917-2.979 7.206-4.725 15.504-4.725 24.017 0 18.513 9.367 30.725 23.534 30.725 7.141 0 12.62-2.462 17.966-8.17l-0.912 6.884h18.433l14.842-91.043h-19.222zm-24.37 73.942c-6.634 0-9.983-4.909-9.983-14.597 0-14.553 6.271-24.875 15.112-24.875 6.695 0 10.32 5.104 10.32 14.508 1e-3 14.681-6.369 24.964-15.449 24.964z" />
                                      <path
                                        d="m233.19 264.26c-2.042-0.236-2.946-0.3-4.346-0.3-11.046 0-16.634 3.788-16.634 11.267 0 4.604 2.73 7.547 6.98 7.547 7.945-1e-3 13.666-7.559 14-18.514zm14.179 32.984h-16.146l0.367-7.663c-4.921 6.054-11.5 8.95-20.421 8.95-10.567 0-17.804-8.25-17.804-20.229 0-18.032 12.591-28.542 34.216-28.542 2.209 0 5.042 0.2 7.938 0.571 0.604-2.442 0.762-3.487 0.762-4.808 0-4.908-3.391-6.73-12.496-6.73-9.537-0.108-17.395 2.272-20.629 3.322 0.204-1.226 2.7-16.638 2.7-16.638 9.709-2.858 16.121-3.93 23.321-3.93 16.738 0 25.604 7.518 25.588 21.705 0.029 3.82-0.605 8.512-1.584 14.675-1.687 10.725-5.32 33.725-5.812 39.317zm261.38-88.592l-3.192 19.767c-6.95-3.496-12-4.921-17.407-4.921-14.551 0-24.75 14.058-24.75 34.107 0 13.821 6.857 22.181 18.183 22.181 4.8 0 10.096-1.492 16.554-4.677l-3.42 20.75c-7.184 1.959-11.816 2.672-17.226 2.672-20.976 0-34.05-15.084-34.05-39.309 0-32.55 18.059-55.3 43.888-55.3 8.507 1e-3 18.562 3.609 21.42 4.73m31.442 55.608c-2.041-0.236-2.941-0.3-4.346-0.3-11.042 0-16.634 3.788-16.634 11.267 0 4.604 2.729 7.547 6.984 7.547 7.937-1e-3 13.662-7.559 13.996-18.514zm14.179 32.984h-16.15l0.37-7.663c-4.924 6.054-11.5 8.95-20.42 8.95-10.563 0-17.804-8.25-17.804-20.229 0-18.032 12.595-28.542 34.212-28.542 2.213 0 5.042 0.2 7.941 0.571 0.601-2.442 0.763-3.487 0.763-4.808 0-4.908-3.392-6.73-12.496-6.73-9.533-0.108-17.396 2.272-20.629 3.322 0.204-1.226 2.704-16.638 2.704-16.638 9.709-2.858 16.116-3.93 23.316-3.93 16.742 0 25.604 7.518 25.583 21.705 0.034 3.82-0.595 8.512-1.579 14.675-1.682 10.725-5.324 33.725-5.811 39.317zm-220.39-1.122c-5.338 1.68-9.496 2.409-14 2.409-9.963 0-15.4-5.726-15.4-16.266-0.138-3.281 1.437-11.881 2.675-19.738 1.12-6.926 8.446-50.533 8.446-50.533h19.367l-2.259 11.212h9.942l-2.646 17.788h-9.975c-2.25 14.091-5.463 31.619-5.496 33.949 0 3.83 2.042 5.483 6.671 5.483 2.22 0 3.938-0.217 5.254-0.692l-2.579 16.388m59.392-0.591c-6.65 2.033-13.08 3.013-19.88 3-21.684-0.021-32.987-11.346-32.987-33.033 0-25.321 14.38-43.95 33.9-43.95 15.97 0 26.17 10.429 26.17 26.8 0 5.433-0.7 10.733-2.382 18.212h-38.575c-1.306 10.741 5.569 15.221 16.837 15.221 6.93 0 13.188-1.434 20.137-4.676l-3.22 18.426zm-10.892-43.912c0.117-1.538 2.059-13.217-9.013-13.217-6.166 0-10.579 4.717-12.375 13.217h21.388zm-123.42-5.004c0 9.365 4.542 15.816 14.842 20.675 7.891 3.708 9.112 4.812 9.112 8.17 0 4.617-3.483 6.7-11.187 6.7-5.817 0-11.225-0.908-17.467-2.92 0 0-2.554 16.32-2.67 17.1 4.42 0.967 8.374 1.85 20.274 2.191 20.567 0 30.059-7.829 30.059-24.746 0-10.18-3.971-16.15-13.738-20.637-8.167-3.758-9.112-4.583-9.112-8.046 0-4 3.245-6.058 9.541-6.058 3.821 0 9.046 0.42 14.004 1.125l2.771-17.18c-5.041-0.8-12.691-1.441-17.146-1.441-21.804 0-29.345 11.379-29.283 25.067m398.45 50.629h-18.437l0.917-6.893c-5.347 5.717-10.825 8.18-17.967 8.18-14.168 0-23.53-12.213-23.53-30.725 0-24.63 14.521-45.393 31.709-45.393 7.558 0 13.28 3.088 18.604 10.096l4.325-26.308h19.221l-14.842 91.043zm-28.745-17.109c9.075 0 15.45-10.283 15.45-24.953 0-9.405-3.63-14.509-10.325-14.509-8.838 0-15.116 10.317-15.116 24.875-1e-3 9.686 3.357 14.587 9.991 14.587zm-56.843-56.929c-2.439 22.917-6.773 46.13-10.162 69.063l-0.891 4.975h19.491c6.971-45.275 8.658-54.117 19.588-53.009 1.742-9.266 4.982-17.383 7.399-21.479-8.163-1.7-12.721 2.913-18.688 11.675 0.471-3.787 1.334-7.466 1.163-11.225h-17.9m-160.42 0c-2.446 22.917-6.78 46.13-10.167 69.063l-0.887 4.975h19.5c6.962-45.275 8.646-54.117 19.569-53.009 1.75-9.266 4.992-17.383 7.4-21.479-8.154-1.7-12.716 2.913-18.678 11.675 0.47-3.787 1.325-7.466 1.162-11.225h-17.899m254.57 68.242c0-3.214 2.596-5.8 5.796-5.8 3.197-3e-3 5.792 2.587 5.795 5.785v0.015c-1e-3 3.2-2.595 5.794-5.795 5.796-3.2-2e-3 -5.794-2.596-5.796-5.796zm5.796 4.404c2.432 1e-3 4.403-1.97 4.403-4.401v-2e-3c3e-3 -2.433-1.968-4.406-4.399-4.408h-4e-3c-2.435 1e-3 -4.408 1.974-4.409 4.408 3e-3 2.432 1.976 4.403 4.409 4.403zm-0.784-1.87h-1.188v-5.084h2.154c0.446 0 0.908 8e-3 1.296 0.254 0.416 0.283 0.654 0.767 0.654 1.274 0 0.575-0.338 1.113-0.888 1.317l0.941 2.236h-1.319l-0.78-2.008h-0.87v2.008 3e-3zm0-2.88h0.654c0.245 0 0.513 0.018 0.729-0.1 0.195-0.125 0.295-0.361 0.295-0.587-9e-3 -0.21-0.115-0.404-0.287-0.524-0.204-0.117-0.542-0.085-0.763-0.085h-0.629v1.296h1e-3z"
                                        fill="#fff"
                                      />
                                    </svg>
                                  )}
                                  {order.cardBrand !== "visa" &&
                                    order.cardBrand !== "mastercard" && (
                                      <>{toHeaderCase(order.cardBrand)}</>
                                    )}
                                </div>
                                <div className="text-xs">
                                  ending in {order.cardLast4}
                                </div>
                              </>
                            )}
                          </dd>
                        </div>
                      </>
                    )}
                  </dl>
                </div>

                {/* Emails sent to this email address */}
                <div className="mt-6 rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5 dark:bg-transparent dark:border-white/5 dark:border pb-1">
                  <dl className="flex flex-wrap mb-5">
                    <div className="flex-auto pl-6 pt-6">
                      <dt className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                        Emails sent to this email address
                      </dt>
                    </div>
                    <div className="mt-6 w-full flex-none gap-x-4 border-t border-gray-900/5 dark:border-white/5 px-6 pt-3">
                      {orderEmails === undefined ? (
                        <div className="pt-2">
                          <SkeletonLoading />
                        </div>
                      ) : (
                        <>
                          {orderEmails.length > 0 ? (
                            <ul
                              role="list"
                              className="mt-3 space-y-6 px-1 -mt-7"
                            >
                              {orderEmails.map(
                                (email: EmailMessage, index: number) => (
                                  <li
                                    key={email.MessageID}
                                    className="relative flex gap-x-4"
                                  >
                                    <div
                                      className={classNames(
                                        index === orderEmails.length - 1
                                          ? "h-6"
                                          : "-bottom-6",
                                        "absolute left-0 top-0 flex w-6 justify-center"
                                      )}
                                    >
                                      <div className="w-px bg-gray-200 dark:bg-white/5" />
                                    </div>
                                    <>
                                      <EnvelopeIcon className="relative mt-3 h-6 w-6 flex-none rounded-full bg-gray-50 text-white dark:text-white/50 dark:bg-dark-secondary" />
                                      <div className="flex-auto rounded-md p-3 ring-1 ring-inset ring-gray-200 dark:ring-white/5">
                                        <div className="flex justify-between gap-x-4 -mb-2">
                                          <div className="py-0.5 text-xs leading-5 text-gray-500">
                                            <span className="font-medium text-gray-900 dark:text-gray-400">
                                              {email.Subject}
                                            </span>
                                          </div>
                                        </div>
                                        <div>
                                          <time
                                            // dateTime={activityItem.dateTime}
                                            className="flex-none py-0.5 text-xs leading-5 text-gray-500"
                                          >
                                            {format(
                                              new Date(email.ReceivedAt),
                                              "dd LLL yyyy @ HH:mm"
                                            )}
                                          </time>
                                        </div>
                                        {email.Status === "Sent" && (
                                          <div className="flex items-center mt-1">
                                            <dd className="rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-inset ring-green-600/20 flex">
                                              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                                              Sent
                                            </dd>
                                          </div>
                                        )}
                                        {email.Attachments &&
                                          email.Attachments.length > 0 && (
                                            <ul
                                              role="list"
                                              className="divide-y divide-gray-200 rounded-md border border-gray-200 dark:border-white/5 mt-2"
                                            >
                                              {email.Attachments.map(
                                                (attachment) => (
                                                  <li
                                                    key={attachment}
                                                    className="flex items-center justify-between py-3 pl-3 pr-4 text-sm"
                                                  >
                                                    <div className="flex w-0 flex-1 items-center">
                                                      <PaperClipIcon
                                                        aria-hidden="true"
                                                        className="h-5 w-5 flex-shrink-0 text-gray-500"
                                                      />
                                                      <span className="ml-2 w-0 flex-1 truncate text-gray-500">
                                                        {attachment}
                                                      </span>
                                                    </div>
                                                    <div className="ml-4 flex-shrink-0"></div>
                                                  </li>
                                                )
                                              )}
                                            </ul>
                                          )}
                                      </div>
                                    </>
                                  </li>
                                )
                              )}
                            </ul>
                          ) : (
                            <>
                              <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                                No emails have been sent to this address
                              </h3>
                              <p className="mt-1 text-sm text-gray-500">
                                We only store email data for 45 days, so if this
                                order was placed more than 45 days ago, it is
                                possible that the email data has been deleted.
                                Or, no emails have been sent to this email
                                address.
                              </p>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    <div className="mt-6 border-t border-gray-900/5 px-6 py-6 pb-0 w-full dark:border-white/5">
                      <p className="text-xs text-gray-500">
                        Note, for PII and GDPR reasons, we only store emails for
                        a max of 45 days from when it was sent. Plus, these are
                        a list of all emails sent to this email and can include
                        emails from other orders.
                      </p>
                    </div>
                  </dl>
                </div>

                {/* Payments related to this order */}
                {order.invoicePayments && order.invoicePayments.length > 0 && (
                  <div className="mt-6 rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5 dark:bg-transparent dark:border dark:border-white/5 pb-1">
                    <dl className="flex flex-wrap mb-5">
                      <div className="flex-auto pl-6 pt-6">
                        <dt className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                          Payments related to this order
                        </dt>
                      </div>
                      <div className="mt-6 w-full flex-none gap-x-4 border-t border-gray-900/5 dark:border-white/5 px-6 pt-6">
                        {order.invoicePayments.map(
                          (invoicePayment: InvoicePayment, index: number) => (
                            <dd
                              className={`pb-4 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400 ${
                                order.invoicePayments &&
                                index != order.invoicePayments.length - 1
                                  ? "border-b border-gray-900/5 dark:border-white/5 mb-3"
                                  : "-mb-2"
                              }`}
                            >
                              {contentLoading ? (
                                <div
                                  role="status"
                                  className="max-w-lg animate-puls mt-4"
                                >
                                  <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                                </div>
                              ) : (
                                <div>
                                  <div className="text-xs">
                                    {format(
                                      new Date(invoicePayment.createdAt),
                                      "dd LLL yyyy"
                                    )}
                                  </div>
                                  <div>&euro;{invoicePayment.total}</div>
                                  <div className="text-xs">
                                    {invoicePayment.invoiceId}
                                  </div>
                                </div>
                              )}
                            </dd>
                          )
                        )}
                      </div>
                    </dl>
                  </div>
                )}

                {/* Ticket Answers Summary */}
                {order &&
                  order.ticketAnswers &&
                  order.ticketAnswers.length > 0 && (
                    <div className="mt-7 rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5 dark:bg-transparent dark:border dark:border-white/5">
                      <dl className="flex flex-wrap mb-5">
                        <div className="flex-auto pl-6 pt-6">
                          <dt className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                            Ticket Answers
                          </dt>
                        </div>
                        <div className="mt-6 w-full flex-none gap-x-4 border-t border-gray-900/5 dark:border-white/5 px-6 pt-6">
                          {order.ticketAnswers.map((answer: TicketAnswer) => (
                            <dd className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-400 mb-3">
                              {contentLoading ? (
                                <div
                                  role="status"
                                  className="max-w-lg animate-puls mt-4"
                                >
                                  <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                                </div>
                              ) : (
                                <div>
                                  {answer.question.question}
                                  <div className="text-xs">{answer.answer}</div>
                                </div>
                              )}
                            </dd>
                          ))}
                        </div>
                      </dl>
                    </div>
                  )}
              </div>

              {/* Invoice */}
              <div className="-mx-4 px-4 py-8 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:rounded-lg sm:px-8 sm:pb-14 lg:col-span-2 lg:row-span-2 lg:row-end-2 xl:px-16 xl:pb-20 xl:pt-16 dark:border dark:border-white/5">
                <div className="flex">
                  <h2 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Order Details
                  </h2>
                  <div className="flex-1 flex justify-end w-full items-center">
                    {!isEditing && (
                      <button
                        onClick={() => setIsEditing(true)}
                        type="button"
                        className="flex items-center  lg:flex text-sm font-semibold leading-6 text-gray-900 dark:text-gray-400"
                      >
                        <div>Edit</div>
                        <div>
                          <PencilSquareIcon className="w-4 h-4 ml-1" />
                        </div>
                      </button>
                    )}
                  </div>
                </div>
                <dl
                  className={`mt-6 ${
                    !isEditing
                      ? "grid grid-cols-1 text-sm leading-6 sm:grid-cols-2"
                      : ""
                  }`}
                >
                  {isEditing ? (
                    <>
                      <Formik
                        initialValues={{
                          firstName: order.firstName || "",
                          lastName: order.lastName || "",
                          emailAddress: order.emailAddress || "",
                        }}
                        validationSchema={Yup.object({
                          firstName: Yup.string().required("Required"),
                          lastName: Yup.string().required("Required"),
                          emailAddress: Yup.string()
                            .email("Invalid email address")
                            .required("Required"),
                        })}
                        onSubmit={handleEditSubmit}
                      >
                        <Form className="grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
                          <div className="sm:pr-4">
                            <dt className="font-semibold text-gray-900 dark:text-white">
                              First Name
                            </dt>
                            <dd className="mt-2 text-gray-500 dark:text-gray-400 -ml-0.5">
                              {contentLoading ? (
                                <div
                                  role="status"
                                  className="max-w-lg animate-puls mt-4"
                                >
                                  <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                                </div>
                              ) : (
                                <Input
                                  name="firstName"
                                  type="text"
                                  required={true}
                                />
                              )}
                            </dd>
                          </div>
                          <div className="sm:pl-4">
                            <dt className="font-semibold text-gray-900 dark:text-white">
                              Last Name
                            </dt>
                            <dd className="mt-2 text-gray-500 dark:text-gray-400 -ml-0.5">
                              {contentLoading ? (
                                <div
                                  role="status"
                                  className="max-w-lg animate-puls mt-4"
                                >
                                  <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                                </div>
                              ) : (
                                <Input
                                  name="lastName"
                                  type="text"
                                  required={true}
                                />
                              )}
                            </dd>
                          </div>
                          <div className="mt-5">
                            <dt className="font-semibold text-gray-900 dark:text-white">
                              Email
                            </dt>
                            <dd className="mt-2 text-gray-500 dark:text-gray-400 -ml-0.5">
                              {contentLoading ? (
                                <div
                                  role="status"
                                  className="max-w-lg animate-puls mt-4"
                                >
                                  <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                                </div>
                              ) : (
                                <Input
                                  name="emailAddress"
                                  type="email"
                                  required={true}
                                />
                              )}
                            </dd>
                            <div className="flex mt-6">
                              <button
                                type="submit"
                                disabled={isLoading}
                                className="mr-3 flex items-center inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                              >
                                {isLoading ? (
                                  <>
                                    <ButtonSpinner />
                                    Processing...
                                  </>
                                ) : (
                                  <>Save</>
                                )}
                              </button>
                              <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                onClick={() => setIsEditing(false)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </Form>
                      </Formik>
                    </>
                  ) : (
                    <>
                      {" "}
                      <div className="sm:pr-4">
                        <dt className="font-semibold text-gray-900 dark:text-white">
                          Email
                        </dt>
                        <dd className="mt-2 text-gray-500 dark:text-gray-400">
                          {contentLoading ? (
                            <div
                              role="status"
                              className="max-w-lg animate-puls mt-4"
                            >
                              <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                            </div>
                          ) : (
                            <>{order?.emailAddress}</>
                          )}
                        </dd>
                      </div>
                      <div className="sm:pl-4">
                        <dt className="font-semibold text-gray-900 dark:text-white">
                          Type
                        </dt>
                        <dd className="mt-2 text-gray-500 dark:text-gray-400">
                          {contentLoading ? (
                            <div
                              role="status"
                              className="max-w-lg animate-puls mt-4"
                            >
                              <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-16 mb-4"></div>
                            </div>
                          ) : (
                            <>{toHeaderCase(order?.orderType)}</>
                          )}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
                <div className="mt-16 w-full">
                  {contentLoading ? (
                    <SkeletonLoading />
                  ) : (
                    <>
                      <table className="w-full whitespace-nowrap text-left text-sm leading-6">
                        <colgroup>
                          <col className="w-full" />
                          <col />
                          <col />
                          <col />
                        </colgroup>
                        <thead className="border-b border-gray-200 dark:border-white/5 text-gray-900 dark:text-white">
                          <tr>
                            <th scope="col" className="px-0 py-3 font-semibold">
                              Item
                            </th>
                            <th
                              scope="col"
                              colSpan={2}
                              className="hidden py-3 pr-0 text-right font-semibold sm:table-cell"
                            >
                              Checked In
                            </th>
                            <th
                              scope="col"
                              className="py-3 pl-8 pr-0 text-right font-semibold"
                            >
                              Price
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {order?.attendees.map((attendee: DashboardAttendee) =>
                            orderId ? (
                              <OrderAttendeeRow
                                attendee={attendee}
                                setViewingAttendee={setViewingAttendee}
                                setViewQRCodeModalOpen={setViewQRCodeModalOpen}
                                orderId={orderId}
                              />
                            ) : null
                          )}

                          {order?.purchasedAddons.addons.map(
                            (purchasedAddon: PurchasedAddonInstance) =>
                              orderId ? (
                                <OrderAddonRow
                                  purchasedAddon={purchasedAddon}
                                  orderId={orderId}
                                />
                              ) : null
                          )}
                        </tbody>
                        <tfoot>
                          {order && order.promoCode && (
                            <tr>
                              <th
                                scope="row"
                                className="px-0 pb-0 pt-6 font-normal text-gray-700 sm:hidden dark:text-gray-400"
                              >
                                <TagIcon className="h-4 w-4 inline mr-1" />{" "}
                                Discount
                              </th>
                              <th
                                scope="row"
                                colSpan={3}
                                className="hidden px-0 pb-0 pt-6 text-right font-normal text-gray-700 sm:table-cell dark:text-gray-400"
                              >
                                <TagIcon className="h-4 w-4 inline mr-1" />{" "}
                                Discount ({order.promoCode.name})
                              </th>
                              <td className="pb-0 pl-8 pr-0 pt-6 text-right tabular-nums text-gray-900 dark:text-gray-400">
                                - &euro;
                                {(
                                  order.attendees.reduce(
                                    (
                                      acc: number,
                                      attendee: DashboardAttendee
                                    ) => {
                                      return (
                                        acc + parseFloat(attendee.ticket.price)
                                      );
                                    },
                                    0
                                  ) -
                                  (parseFloat(order?.total) +
                                    parseFloat(order?.purchasedAddons.total) -
                                    (parseFloat(order?.fees) +
                                      parseFloat(
                                        order?.purchasedAddons.feeTotal
                                      )))
                                ).toFixed(2)}
                              </td>
                            </tr>
                          )}
                          {order &&
                            order.carbonFootprintContribution &&
                            parseFloat(order.carbonFootprintContribution) >
                              0 && (
                              <tr>
                                <th
                                  scope="row"
                                  className="px-0 pb-0 pt-6 font-normal text-gray-700 sm:hidden dark:text-gray-400"
                                >
                                  Carbon Footprint Contribution
                                </th>
                                <th
                                  scope="row"
                                  colSpan={3}
                                  className="relative hidden px-0 pb-0  text-right font-normal text-gray-700 sm:table-cell dark:text-gray-400"
                                >
                                  <div className="absolute right-0">
                                    <img
                                      src="/leaf-solid.png"
                                      className="w-4 absolute left-0 -ml-6 top-0 mt-1"
                                    />
                                    Carbon Footprint Contribution
                                  </div>
                                </th>
                                <td className="pb-0 pl-8 pr-0 pt-6 text-right tabular-nums text-gray-900 dark:text-gray-400">
                                  &euro;
                                  {parseFloat(
                                    order.carbonFootprintContribution
                                  ).toFixed(2)}
                                </td>
                              </tr>
                            )}
                          <tr>
                            <th
                              scope="row"
                              className="px-0 pb-0 pt-6 font-normal text-gray-700 sm:hidden dark:text-gray-400"
                            >
                              Subtotal
                            </th>
                            <th
                              scope="row"
                              colSpan={3}
                              className="hidden px-0 pb-0 pt-6 text-right font-normal text-gray-700 sm:table-cell dark:text-gray-400"
                            >
                              Subtotal
                            </th>
                            <td className="pb-0 pl-8 pr-0 pt-6 text-right tabular-nums text-gray-900 dark:text-gray-400">
                              &euro;
                              {(
                                parseFloat(order?.total) +
                                parseFloat(order?.purchasedAddons.total) -
                                (parseFloat(order?.fees) +
                                  parseFloat(order?.purchasedAddons.feeTotal))
                              ).toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <th
                              scope="row"
                              className="pt-4 font-normal text-gray-700 sm:hidden dark:text-gray-400"
                            >
                              Fees
                            </th>
                            <th
                              scope="row"
                              colSpan={3}
                              className="hidden pt-4 text-right font-normal text-gray-700 sm:table-cell dark:text-gray-400"
                            >
                              Fees
                            </th>
                            <td className="pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900 dark:text-gray-400">
                              &euro;{parseFloat(order?.fees).toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <th
                              scope="row"
                              className="pt-4 font-semibold text-gray-900 sm:hidden dark:text-gray-400"
                            >
                              Total
                            </th>
                            <th
                              scope="row"
                              colSpan={3}
                              className="hidden pt-4 text-right font-semibold text-gray-900 sm:table-cell dark:text-gray-400"
                            >
                              Total
                            </th>
                            <td className="pb-0 pl-8 pr-0 pt-4 text-right font-semibold tabular-nums text-gray-900 dark:text-gray-400">
                              &euro;{parseFloat(order?.total).toFixed(2)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
};
